import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  Building2,
  ChevronLeft,
  Info,
  Lock,
  MapPin,
  Plus,
  Save,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button";
import { Skeleton } from "../../components/ui/Skeleton";
import { SectionCard } from "../../components/ui/SectionCard";
import { FieldLabel } from "../../components/ui/FieldLabel";
import { Input } from "../../components/ui/Input";
import { cn } from "../../utils/cn";
import {
  createHoldingCompany,
  getHoldingCompany,
  getHoldingMe,
  updateHoldingCompany,
} from "../../api/holding";
import { getRegions, getCommunes } from "../../api/contracts";
import { apiBodyMessage } from "../../utils/apiErrorMessage";
import { useCountry } from "../../context/CountryContext";

const ADDRESS_TYPES = [
  { value: "Bodega", label: "Bodega" },
  { value: "Oficina", label: "Oficina" },
];

const INTEGRATION_NAMES = [
  { value: "Sendu", label: "Sendu" },
  { value: "Mercadolibre", label: "Mercado Libre" },
  { value: "Shopify", label: "Shopify" },
  { value: "WooCommerce", label: "WooCommerce" },
  { value: "Falabella", label: "Falabella" },
  { value: "Ripley", label: "Ripley" },
  { value: "Prestashop", label: "Prestashop" },
];

// Slots de 30 min entre 10:30 y 14:30 inclusive.
const PICKUP_TIMES = (() => {
  const opts = [];
  for (let mins = 10 * 60 + 30; mins <= 14 * 60 + 30; mins += 30) {
    const h = String(Math.floor(mins / 60)).padStart(2, "0");
    const m = String(mins % 60).padStart(2, "0");
    const v = `${h}:${m}`;
    opts.push({ value: v, label: v });
  }
  return opts;
})();

const EMPTY = {
  name: "",
  legalName: "",
  fiscalAddress: "",
  // Defaults fijos: estos campos no se muestran en el form pero viajan en el
  // payload tanto en creación como en edición (en edición se preservan los
  // valores que vengan del backend gracias a `{ ...EMPTY, ...res }`).
  // `operationsCountry`/`operationsCountryId` NO están acá: dependen del país
  // del holding (hook), y se completan en `submit` solo si el doc no los trae.
  businessIdentifierType: "RUT",
  pickUpTime: "",
  ff: false,
  workingDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  shippingAddresses: [
    {
      address: "",
      type: "Bodega",
      municipality: "",
      state: "",
      contactPhone: "",
      senderIds: [],
    },
  ],
  contacts: {
    operationsContacts: [{ name: "", email: "", phone: "", role: "OPERACIONES" }],
    billingContacts: [{ name: "", email: "", phone: "", role: "FACTURACION Y COBRANZA" }],
  },
};

export default function HoldingCompanyForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [holding, setHolding] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // True when the company could not be loaded. The form is then not rendered,
  // so a failed load can never submit the EMPTY doc as if it were the company.
  const [loadFailed, setLoadFailed] = useState(false);

  // Regiones [{ code, name }] y caché de comunas keyed por regionCode.
  // Las direcciones guardan `state` y `municipality` como nombres (string),
  // así que mantenemos esta tabla para mapear `name → code` y filtrar comunas.
  const [regionsList, setRegionsList] = useState([]);
  const [communesByRegion, setCommunesByRegion] = useState({});

  // País del holding. En edición gana el que ya tiene el documento (una empresa
  // hija no cambia de país por abrir el formulario).
  const { countryId: userCountryId, countryName, loading: countryLoading } = useCountry();
  const countryId = form.operationsCountryId || userCountryId;

  const getRegionCodeByName = (name) =>
    regionsList.find((r) => r.name === name)?.code;

  useEffect(() => {
    // getHoldingMe lee del JWT — sincrono
    const h = getHoldingMe();
    if (h) setHolding(h);
  }, []);

  // En "Nueva empresa hija" la dirección fiscal viene fijada por el holding
  // padre: street + city + state. Es heredada y no editable.
  const inheritedFiscalAddress = holding?.fiscalAddress
    ? [
        holding.fiscalAddress.street,
        holding.fiscalAddress.city,
        holding.fiscalAddress.state,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  useEffect(() => {
    if (!isEdit && inheritedFiscalAddress) {
      setForm((p) =>
        p.fiscalAddress === inheritedFiscalAddress
          ? p
          : { ...p, fiscalAddress: inheritedFiscalAddress },
      );
    }
  }, [isEdit, inheritedFiscalAddress]);

  // Carga regiones del país operacional una sola vez (o cuando cambia el país).
  useEffect(() => {
    if (!countryId) return undefined;
    let active = true;
    getRegions({ countryId }).then((data) => {
      if (!active || data?.error || !Array.isArray(data)) return;
      setRegionsList(data);
    });
    return () => {
      active = false;
    };
  }, [countryId]);

  // Asegura que las comunas de cada región usada en las direcciones estén
  // cacheadas. Cubre dos casos: carga inicial en modo edición y cambios de
  // región por parte del usuario.
  useEffect(() => {
    if (!regionsList.length || !countryId) return;
    const codesNeeded = new Set();
    for (const a of form.shippingAddresses || []) {
      const code = getRegionCodeByName(a?.state);
      if (code && !communesByRegion[code]) codesNeeded.add(code);
    }
    if (!codesNeeded.size) return;
    let active = true;
    Promise.all(
      Array.from(codesNeeded).map((code) =>
        getCommunes({ countryId, regionId: code }).then((data) => ({ code, data })),
      ),
    ).then((results) => {
      if (!active) return;
      setCommunesByRegion((prev) => {
        const next = { ...prev };
        for (const { code, data } of results) {
          if (!data?.error && Array.isArray(data)) next[code] = data;
        }
        return next;
      });
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionsList, form.shippingAddresses, countryId]);

  useEffect(() => {
    // A route-param change does not remount this component (same element type in
    // the same position), so the previous company's outcome is cleared first —
    // otherwise a failed load would keep its alert, and the previous company's
    // data would stay editable, while another id loads.
    setLoading(isEdit);
    setLoadFailed(false);
    setError("");
    if (!isEdit) return;
    let active = true;
    getHoldingCompany(id)
      .then((res) => {
        if (!active) return;
        if (res?.error) {
          // Only a server response (object) carries backend text; a string is
          // the fixed network-failure fallback from api/holding.js.
          const backendMsg =
            typeof res.error === "object" ? apiBodyMessage(res.error) : null;
          setError(backendMsg ?? t("No se pudo cargar la empresa."));
          setLoadFailed(true);
          return;
        }
        setForm({ ...EMPTY, ...res });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const previewBusinessIdentifier =
    holding && !isEdit
      ? `${holding.businessIdentifier}${(holding.nextChildCounter ?? 0) + 1}`
      : null;

  const onChange = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let target = next;
      for (let i = 0; i < parts.length - 1; i++) {
        target[parts[i]] = { ...target[parts[i]] };
        target = target[parts[i]];
      }
      target[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updateAddress = (idx, key, value) => {
    setForm((prev) => {
      const list = prev.shippingAddresses.map((a, i) =>
        i === idx ? { ...a, [key]: value } : a,
      );
      return { ...prev, shippingAddresses: list };
    });
  };

  const addAddress = () =>
    setForm((p) => ({
      ...p,
      shippingAddresses: [
        ...p.shippingAddresses,
        {
          address: "",
          type: "Bodega",
          municipality: "",
          state: "",
          contactPhone: "",
          senderIds: [],
        },
      ],
    }));

  const removeAddress = (idx) =>
    setForm((p) => ({
      ...p,
      shippingAddresses: p.shippingAddresses.filter((_, i) => i !== idx),
    }));

  const addSenderId = (addrIdx) =>
    setForm((p) => ({
      ...p,
      shippingAddresses: p.shippingAddresses.map((a, i) =>
        i === addrIdx
          ? {
              ...a,
              senderIds: [
                ...(a.senderIds || []),
                { sender_id: "", integrationName: "Sendu", alias: "" },
              ],
            }
          : a,
      ),
    }));

  const removeSenderId = (addrIdx, sIdx) =>
    setForm((p) => ({
      ...p,
      shippingAddresses: p.shippingAddresses.map((a, i) =>
        i === addrIdx
          ? { ...a, senderIds: (a.senderIds || []).filter((_, j) => j !== sIdx) }
          : a,
      ),
    }));

  const updateSenderId = (addrIdx, sIdx, key, value) =>
    setForm((p) => ({
      ...p,
      shippingAddresses: p.shippingAddresses.map((a, i) => {
        if (i !== addrIdx) return a;
        const list = (a.senderIds || []).map((s, j) =>
          j === sIdx
            ? {
                ...s,
                [key]: value,
                // El alias por defecto sigue al sender_id mientras el usuario
                // no lo edite manualmente.
                ...(key === "sender_id" && (!s.alias || s.alias === s.sender_id)
                  ? { alias: value }
                  : {}),
              }
            : s,
        );
        return { ...a, senderIds: list };
      }),
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (countryLoading || loadFailed) return; // sin país resuelto o sin datos cargados
    setSaving(true);
    setError("");
    // El país solo se completa cuando el documento no lo trae (creación, o
    // empresas legadas): nunca se pisa el país ya guardado.
    const payload = {
      ...form,
      operationsCountry: form.operationsCountry || countryName,
      operationsCountryId: form.operationsCountryId || userCountryId,
    };
    delete payload.businessIdentifier;
    delete payload.holdingId;
    delete payload._id;

    const res = isEdit
      ? await updateHoldingCompany(id, payload)
      : await createHoldingCompany(payload);
    setSaving(false);

    if (res?.error) {
      // Same rule as the load: only a response body (object) is backend text.
      const backendMsg =
        res.error && typeof res.error === "object" ? apiBodyMessage(res.error) : null;
      setError(backendMsg ?? t("No se pudo guardar la empresa."));
      return;
    }
    navigate("/holding/companies");
  };

  // Shared by the normal view and the load-failure view: on a failed load the
  // header stays (so "Volver a empresas" is reachable) but the form does not.
  const header = (
    <header className="space-y-1">
      <button
        type="button"
        onClick={() => navigate("/holding/companies")}
        className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg transition-colors"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        {t("Volver a empresas")}
      </button>
      <h1 className="text-2xl font-semibold text-fg flex items-center gap-2">
        <Building2 className="w-6 h-6 text-fg-muted" />
        {isEdit ? t("Editar empresa") : t("Nueva empresa hija")}
      </h1>
      <p className="text-sm text-fg-muted">
        {isEdit
          ? t("Modificá los datos operativos y direcciones de retiro de la empresa.")
          : t("Registrá una nueva empresa hija dentro de tu holding.")}
      </p>
    </header>
  );

  // `error` is always a string (backend text or a translated fallback).
  const errorBanner = error ? (
    <div
      role="alert"
      className="rounded-lg border border-status-error/30 bg-status-error-soft px-3 py-2 text-sm text-status-error flex items-center gap-2"
    >
      <AlertCircle className="w-4 h-4 shrink-0" />
      <span>{error}</span>
    </div>
  ) : null;

  if (loading) {
    // Espeja el header real (back link + ícono+título + subtítulo) y el grid de
    // 5 columnas con las dos SectionCards (Datos generales col-span-2 + el resto).
    return (
      <div className="w-full min-w-0 p-6 space-y-5 lg:h-[calc(100vh-4rem)] lg:overflow-hidden lg:flex lg:flex-col" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        <header className="space-y-1">
          <Skeleton className="h-3 w-32" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-7 w-48" />
          </div>
          <Skeleton className="h-4 w-80 max-w-full" />
        </header>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 rounded-xl border border-border bg-bg p-5 space-y-3">
            <Skeleton className="h-4 w-40" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-1.5">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-9 w-full rounded-lg" />
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-3 rounded-xl border border-border bg-bg p-5 space-y-3">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="h-28 w-full rounded-lg" />
            <Skeleton className="h-28 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    // No form and no RUT banners: the backend text stays visible and the only
    // way out is back to the list, so nothing can be submitted with stale data.
    return (
      <div className="w-full min-w-0 p-6 space-y-5">
        {header}
        {errorBanner}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 p-6 space-y-5 lg:h-[calc(100vh-4rem)] lg:overflow-hidden lg:flex lg:flex-col">
      {header}

      {/* Banner: RUT preview (creación) o RUT actual (edición) */}
      {isEdit && form.businessIdentifier ? (
        <div className="rounded-lg border border-border bg-bg-muted/40 p-3 flex items-start gap-2">
          <Lock className="w-4 h-4 text-fg-muted shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="text-fg-muted">{t("Identificador fiscal")}: </span>
            <span className="font-mono font-semibold text-fg">{form.businessIdentifier}</span>
            <p className="text-xs text-fg-muted mt-0.5">{t("No se puede modificar.")}</p>
          </div>
        </div>
      ) : previewBusinessIdentifier ? (
        <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-3 flex items-start gap-2">
          <Info className="w-4 h-4 text-brand-700 shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <p className="text-fg">
              {t("El identificador fiscal se asignará automáticamente al crear la empresa.")}
            </p>
            <p className="mt-1">
              <span className="text-fg-muted">{t("Nuevo RUT")}: </span>
              <span className="font-mono font-semibold text-brand-700">
                {previewBusinessIdentifier}
              </span>
            </p>
          </div>
        </div>
      ) : null}

      {errorBanner}

      <form onSubmit={submit} className="space-y-5 lg:flex-1 lg:flex lg:flex-col lg:min-h-0">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:flex-1 lg:min-h-0">
        <SectionCard
          tone="bg"
          icon={<Building2 className="w-4 h-4 text-fg-muted" />}
          title={t("Datos generales")}
          className="lg:col-span-2 lg:self-start"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label={t("Nombre")}
              value={form.name}
              onChange={(v) => onChange("name", v)}
              required
              placeholder={t("Ej: Sendu Biofoods")}
            />
            <Field
              label={t("Razón social")}
              value={form.legalName}
              onChange={(v) => onChange("legalName", v)}
              required
              placeholder={t("Ej: SENDU BIOFOODS SPA")}
            />
            <Field
              label={t("Dirección fiscal")}
              value={form.fiscalAddress}
              onChange={(v) => onChange("fiscalAddress", v)}
              required
              disabled={!isEdit}
              hint={!isEdit ? t("Heredada del holding") : undefined}
              wide
            />
            <SelectField
              label={t("Hora de retiro")}
              value={form.pickUpTime}
              onChange={(v) => onChange("pickUpTime", v)}
              options={PICKUP_TIMES}
              placeholder={t("Seleccione...")}
            />
          </div>
        </SectionCard>

        <SectionCard
          tone="bg"
          icon={<MapPin className="w-4 h-4 text-fg-muted" />}
          title={t("Direcciones de retiro")}
          subtitle={t("Lugares físicos donde el equipo logístico retirará los paquetes.")}
          action={
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addAddress}
              iconLeft={<Plus className="w-4 h-4" />}
            >
              {t("Agregar")}
            </Button>
          }
          className="lg:col-span-3 lg:flex lg:flex-col lg:min-h-0 lg:h-full"
          bodyClassName="lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-2"
        >
          <div className="space-y-3">
            {form.shippingAddresses.map((a, idx) => {
              const regionCode = getRegionCodeByName(a?.state);
              const communeOptions = regionCode
                ? (communesByRegion[regionCode] || []).map((c) => ({
                    value: c.name,
                    label: c.name,
                  }))
                : [];
              const regionOptions = regionsList.map((r) => ({
                value: r.name,
                label: r.name,
              }));
              const communesLoaded = !!(regionCode && communesByRegion[regionCode]);
              return (
                <div
                  key={idx}
                  className="rounded-lg border border-border bg-bg-muted/30 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-fg">
                      {t("Dirección")} {idx + 1}
                    </h3>
                    {form.shippingAddresses.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAddress(idx)}
                        className="inline-flex items-center gap-1 text-xs text-status-error hover:underline"
                        aria-label={`${t("Eliminar dirección")} ${idx + 1}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t("Eliminar")}
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={t("Calle y número")}
                      value={a.address}
                      onChange={(v) => updateAddress(idx, "address", v)}
                      placeholder={t("Ej: Av. Apoquindo 123")}
                      wide
                      required
                    />
                    <SelectField
                      label={t("Tipo")}
                      value={a.type}
                      onChange={(v) => updateAddress(idx, "type", v)}
                      options={ADDRESS_TYPES.map((o) => ({ value: o.value, label: t(o.label) }))}
                      placeholder={t("Seleccione...")}
                    />
                    <SelectField
                      label={t("Región")}
                      value={a.state}
                      onChange={(v) =>
                        setForm((prev) => {
                          const list = prev.shippingAddresses.map((row, i) =>
                            i === idx ? { ...row, state: v, municipality: "" } : row,
                          );
                          return { ...prev, shippingAddresses: list };
                        })
                      }
                      options={regionOptions}
                      placeholder={regionsList.length ? t("Seleccione...") : t("Cargando...")}
                      disabled={!regionsList.length}
                    />
                    <SelectField
                      label={t("Comuna")}
                      value={a.municipality}
                      onChange={(v) => updateAddress(idx, "municipality", v)}
                      options={communeOptions}
                      placeholder={
                        !a.state
                          ? t("Primero seleccione región")
                          : communesLoaded
                          ? t("Seleccione...")
                          : t("Cargando...")
                      }
                      disabled={!a.state || !communesLoaded}
                    />
                    <Field
                      label={t("Teléfono de contacto")}
                      value={a.contactPhone}
                      onChange={(v) => updateAddress(idx, "contactPhone", v)}
                      placeholder="+56 9 1234 5678"
                      type="tel"
                      wide
                    />
                  </div>

                  <SenderIdsBlock
                    senderIds={a.senderIds || []}
                    onAdd={() => addSenderId(idx)}
                    onRemove={(sIdx) => removeSenderId(idx, sIdx)}
                    onChange={(sIdx, key, value) => updateSenderId(idx, sIdx, key, value)}
                  />
                </div>
              );
            })}
          </div>
        </SectionCard>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 lg:flex-shrink-0">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/holding/companies")}
            disabled={saving}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            loading={saving}
            iconLeft={!saving ? <Save className="w-4 h-4" /> : undefined}
          >
            {saving
              ? t("Guardando...")
              : isEdit
              ? t("Guardar cambios")
              : t("Crear empresa")}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Composición específica del form: label + ui/Input con manejo de `wide` y `hint`.
// Mantenemos esta envoltura local porque el campo aquí siempre vive en una
// grid de 2 columnas y necesita el `sm:col-span-2` para columnas anchas.
function Field({
  label,
  value,
  onChange,
  required,
  disabled,
  hint,
  placeholder,
  type = "text",
  wide,
}) {
  return (
    <div className={cn("space-y-1", wide && "sm:col-span-2")}>
      <FieldLabel>{label}</FieldLabel>
      <Input
        type={type}
        required={required}
        disabled={disabled}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && (
        <p className="text-[11px] text-fg-muted flex items-center gap-1">
          <Lock className="w-3 h-3" />
          {hint}
        </p>
      )}
    </div>
  );
}

function SenderIdsBlock({ senderIds, onAdd, onRemove, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-dashed border-border bg-bg p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-fg-muted uppercase tracking-wider">
          <Tag className="w-3.5 h-3.5" />
          {t("Sender IDs de esta dirección")}
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
        >
          <Plus className="w-3.5 h-3.5" />
          {t("Agregar sender ID")}
        </button>
      </div>

      {senderIds.length === 0 ? (
        <p className="text-xs text-fg-muted">
          {t("Sin sender IDs registrados. Agregá uno si esta dirección opera bajo una integración (Sendu, Mercado Libre, etc.).")}
        </p>
      ) : (
        <ul className="space-y-2">
          {senderIds.map((s, sIdx) => (
            <li
              key={sIdx}
              className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end"
            >
              <div className="space-y-1">
                <label className="text-[10px] font-semibold text-fg-muted uppercase tracking-wider block">
                  {t("Integración")}
                </label>
                <select
                  value={s.integrationName || ""}
                  onChange={(e) => onChange(sIdx, "integrationName", e.target.value)}
                  className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                >
                  <option value="">{t("Seleccione...")}</option>
                  {INTEGRATION_NAMES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-semibold text-fg-muted uppercase tracking-wider block">
                  Sender ID
                </label>
                <input
                  type="text"
                  value={s.sender_id || ""}
                  onChange={(e) => onChange(sIdx, "sender_id", e.target.value)}
                  placeholder={t("Ej: 861867")}
                  required
                  className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <button
                type="button"
                onClick={() => onRemove(sIdx)}
                className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border bg-bg text-fg-muted hover:text-status-error hover:border-status-error/40 transition-colors"
                aria-label={t("Eliminar sender ID")}
                title={t("Eliminar")}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
  disabled,
  wide,
}) {
  return (
    <div className={cn("space-y-1", wide && "sm:col-span-2")}>
      <FieldLabel>{label}</FieldLabel>
      <select
        required={required}
        disabled={disabled}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-fg",
          "focus:outline-none focus:ring-2 focus:ring-brand-500",
          "disabled:bg-bg-muted/40 disabled:text-fg-muted disabled:cursor-not-allowed",
        )}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
